import * as React from 'react'
import * as O from 'fp-ts/lib/Option'
import { pipe } from 'fp-ts/lib/pipeable'

import { faDownload } from '@fortawesome/pro-light-svg-icons/faDownload'
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { AttachmentError } from '../../domain/attachment-error'
import { AttachmentSupport, extensionOf } from '../../domain/attachment-support'
import { AttachmentModel } from '../../domain/model'
import { AttachmentTranslations } from '../../translations'

interface UploadedFileProps {
  model: AttachmentModel
  onDelete?: (model: AttachmentModel) => void
  authProfile: string
  attachmentApiUrl: string
  translations: AttachmentTranslations
}
function UploadedFile(props: UploadedFileProps) {
  const { model, onDelete, attachmentApiUrl } = props

  return (
    <div className="uploaded-file" data-qa="uploadedFile">
      <span className="icon file-control-margin">
        <FontAwesomeIcon
          icon={AttachmentSupport.getFileIcon(model.attachment.name)}
        />
      </span>
      <span className="file-name file-control-margin" data-qa="name">
        {model.attachment.name}
      </span>

      {isUploaded(model) && (
        <a
          href={`${attachmentApiUrl}${
            model.attachment.path
          }?attachment-auth-profile=${props.authProfile}${pipe(
            model.attachment.version,
            O.map((v) => `&version=${v}`),
            O.getOrElse(() => '')
          )}`}
          className="button file-control-margin"
          data-qa="download"
          target="_blank"
        >
          <FontAwesomeIcon icon={faDownload} />
        </a>
      )}
      {onDelete && (
        <button
          className="button file-control-margin"
          onClick={handleDelete}
          data-qa="uploadedFileDelete"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
      {renderProgress()}
      {renderInfo(props.translations)}
    </div>
  )

  function handleDelete() {
    if (typeof props.onDelete === 'function') {
      props.onDelete(props.model)
    }
  }

  function renderProgress() {
    switch (model.kind) {
      case 'UploadedAttachment':
      case 'PublishedAttachment':
        return ''
      case 'LoadingAttachment':
        return (
          <div
            className="progress-bar file-control-margin"
            data-qa="uploadedFileProgress"
          >
            <div style={{ width: `${model.progress}%` }} />
          </div>
        )
      case 'ErroredAttachment':
        return (
          <div
            className="progress-bar file-control-margin is-danger"
            data-qa="uploadedFileProgress"
          >
            <div style={{ width: '0%' }} />
          </div>
        )
    }
  }

  function renderInfo(t: AttachmentTranslations) {
    switch (model.kind) {
      case 'UploadedAttachment':
      case 'PublishedAttachment':
        return ''
      case 'LoadingAttachment':
        return <div className="file-status-info">{t.loading}</div>
      case 'ErroredAttachment':
        const error = formatError(model.error, t)
        return (
          <div
            data-qa={`error-${model.error.kind}`}
            className="file-status-info is-danger"
          >
            {error}
          </div>
        )
    }
  }

  function formatError(
    error: AttachmentError.Type,
    t: AttachmentTranslations
  ): string {
    switch (error.kind) {
      case 'InvalidExtension':
        const ext = O.toUndefined(extensionOf(error.file.name))
        return t.error[error.kind](ext ? ext : '')
      case 'FileTooBig':
      case 'InvalidMimeType':
      case 'MissingExtension':
      case 'Unknown':
        return t.error[error.kind]
      case 'MissingVersion':
      case 'PublishFailure':
        return t.error.PublishFailure

      default:
        const _: never = error
        return _
    }
  }

  function isUploaded(x: AttachmentModel) {
    return x.kind === 'UploadedAttachment' || x.kind === 'PublishedAttachment'
  }
}

export default UploadedFile
